import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbDateStruct, NgbInputDatepicker } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Board } from 'src/app/core/models/board.model';
import { UserType } from 'src/app/core/models/userType.models';
import { FusionConnectDailyStat, FusionConnectRead } from 'src/app/core/models/fusion-connect.model';
import { BoardService } from 'src/app/core/services/board.service';
import { FusionConnectService } from 'src/app/core/services/fusion-connect.service';
import { LoginService } from 'src/app/core/services/login.service';

type GroupByMode = 'board' | 'location' | 'salesRep';
type PeriodMode = 'daily' | 'weekly' | 'monthly' | 'yearly';

interface AggregatedStatsRow {
  key: string;
  periodLabel: string;
  groupKey: string;
  groupLabel: string;
  gameLabel: string;
  boardCount: number;
  coinIn: number;
  coinOut: number;
  cashIn: number;
  cashOut: number;
  gross: number;
  gamesPlayed: number;
  gamesWon: number;
  reads: number;
  net: number;
}

interface MachineDetailRow {
  key: string;
  boardId: string;
  boardLabel: string;
  locationName: string;
  salesRepName: string;
  gameName: string;
  gameVersion: string;
  coinIn: number;
  coinOut: number;
  cashIn: number;
  cashOut: number;
  gross: number;
  gamesPlayed: number;
  gamesWon: number;
  reads: number;
  net: number;
  lastStatDate: string;
}

@Component({
  selector: 'app-fusion-connect-stats',
  templateUrl: './fusion-connect-stats.component.html',
  styleUrls: ['./fusion-connect-stats.component.scss']
})
export class FusionConnectStatsComponent implements OnInit {
  @ViewChild('detailsSection') detailsSection?: ElementRef<HTMLElement>;

  breadCrumbItems = [{ label: 'Fortune' }, { label: 'Fortune Stats', active: true }];

  filters: FormGroup;
  loading = false;
  generating = false;
  rows: AggregatedStatsRow[] = [];
  rawStats: FusionConnectDailyStat[] = [];
  liveReads: FusionConnectRead[] = [];
  boards: Board[] = [];
  mergedStats: FusionConnectDailyStat[] = [];
  detailRows: MachineDetailRow[] = [];
  selectedRow: AggregatedStatsRow | null = null;
  lastLoadedAt: Date = new Date();
  comparisonChartOptions: any;
  distributionChartOptions: any;
  trendChartOptions: any;
  readonly currentMonth = this.toDateStruct(new Date());

  private boardsMap = new Map<string, Board>();
  private isFusionConnectBoard = (b: any) =>
    b?.fusionConnect === true ||
    b?.fusionConnect === 1 ||
    b?.fusionConnect === '1' ||
    b?.fusionConnect === 'true';

  constructor(
    private fb: FormBuilder,
    private fusionConnectService: FusionConnectService,
    private boardService: BoardService,
    private toastr: ToastrService,
    private loginService: LoginService
  ) {
    this.filters = this.fb.group({
      from: [null, Validators.required],
      to: [null, Validators.required],
      boardId: [''],
      groupBy: ['location' as GroupByMode],
      period: ['daily' as PeriodMode],
      includeTodayLive: [true],
    });
  }

  async ngOnInit(): Promise<void> {
    await this.loadBoards();
    await this.loadReport();
  }

  async loadBoards(): Promise<void> {
    try {
      const boards = await this.boardService.getAll();
      this.boards = (boards || [])
        .filter((b) => this.isFusionConnectBoard(b))
        .sort((a, b) => (a?.assetTag || '').localeCompare(b?.assetTag || ''));

      this.boardsMap.clear();
      this.boards.forEach((board) => {
        if (board?.id) {
          this.boardsMap.set(board.id, board);
        }
      });
    } catch (err) {
      console.error('[FusionStats] failed to load boards', err);
    }
  }

  async loadReport(): Promise<void> {
    if (this.filters.invalid) {
      return;
    }

    this.loading = true;
    try {
      const values = this.filters.getRawValue();
      const from = this.toIsoDate(values.from);
      const to = this.toIsoDate(values.to);

      if (!from || !to) {
        return;
      }

      const boardId = values.boardId || undefined;

      const dailyRes = await this.fusionConnectService.fetchDailyStats({
        from,
        to,
        boardId,
        page: 0,
        pageSize: 500,
      });

      this.rawStats = dailyRes.content ?? [];

      let mergedStats = [...this.rawStats];
      if (values.includeTodayLive && this.includesToday(from, to)) {
        const liveRes = await this.fusionConnectService.fetchReads({
          page: 0,
          pageSize: 500,
          boardId,
        });
        this.liveReads = liveRes.content ?? [];
        mergedStats = this.mergeTodayLive(mergedStats, this.liveReads);
      } else {
        this.liveReads = [];
      }

      this.mergedStats = mergedStats;
      this.rows = this.aggregateStats(
        mergedStats,
        values.groupBy as GroupByMode,
        values.period as PeriodMode
      );
      this.refreshCharts();
      this.selectedRow = this.rows.length ? this.rows[0] : null;
      this.detailRows = this.selectedRow ? this.buildMachineDetails(this.selectedRow) : [];
      this.lastLoadedAt = new Date();
    } catch (err) {
      console.error('[FusionStats] failed to load report', err);
      this.toastr.error('Failed to load Fortune stats report');
    } finally {
      this.loading = false;
    }
  }

  resetFilters(): void {
    this.filters.reset({
      from: null,
      to: null,
      boardId: '',
      groupBy: 'location',
      period: 'daily',
      includeTodayLive: true,
    });
    this.rows = [];
    this.rawStats = [];
    this.liveReads = [];
    this.mergedStats = [];
    this.detailRows = [];
    this.selectedRow = null;
    this.refreshCharts();
  }

  async generateStatsFromReads(): Promise<void> {
    if (this.filters.invalid || this.generating) {
      return;
    }

    this.generating = true;
    try {
      const values = this.filters.getRawValue();
      const from = this.toIsoDate(values.from);
      const to = this.toIsoDate(values.to);

      if (!from || !to) {
        return;
      }

      const res = await this.fusionConnectService.rebuildDailyStats({
        from,
        to,
      });
      this.toastr.success(res?.message || 'Fortune stats generated from existing reads');
      await this.loadReport();
    } catch (err) {
      console.error('[FusionStats] failed to generate stats from reads', err);
      this.toastr.error('Failed to generate stats from existing Fortune reads');
    } finally {
      this.generating = false;
    }
  }

  get totalCashIn(): number {
    return this.rows.reduce((sum, row) => sum + row.cashIn, 0);
  }

  get totalNet(): number {
    return this.rows.reduce((sum, row) => sum + row.net, 0);
  }

  get totalReads(): number {
    return this.rows.reduce((sum, row) => sum + row.reads, 0);
  }

  get showOperationalSummaryCards(): boolean {
    return this.loginService.userType === UserType.EXTERNAL;
  }

  trackByRow = (_: number, row: AggregatedStatsRow) => row.key;
  trackByDetail = (_: number, row: MachineDetailRow) => row.key;

  selectRow(row: AggregatedStatsRow): void {
    this.selectedRow = row;
    this.detailRows = this.buildMachineDetails(row);

    if (!this.detailRows.length) {
      this.toastr.info('No machine rows were found for that selection yet.');
      return;
    }

    setTimeout(() => {
      this.detailsSection?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  }

  exportRowsCsv(): void {
    const rows = this.rows.map((row) => ({
      period: row.periodLabel,
      group: row.groupLabel,
      game: row.gameLabel,
      boards: row.boardCount,
      coinIn: row.coinIn,
      coinOut: row.coinOut,
      cashIn: row.cashIn,
      cashOut: row.cashOut,
      gross: row.gross,
      gamesPlayed: row.gamesPlayed,
      gamesWon: row.gamesWon,
      reads: row.reads,
      net: row.net,
    }));
    this.downloadCsv('fortune-stats-report.csv', rows);
  }

  exportDetailsCsv(): void {
    const rows = this.detailRows.map((row) => ({
      board: row.boardLabel,
      location: row.locationName,
      salesRep: row.salesRepName,
      game: row.gameName,
      gameVersion: row.gameVersion,
      cashIn: row.cashIn,
      cashOut: row.cashOut,
      gross: row.gross,
      coinIn: row.coinIn,
      coinOut: row.coinOut,
      gamesPlayed: row.gamesPlayed,
      gamesWon: row.gamesWon,
      reads: row.reads,
      net: row.net,
      lastStatDate: row.lastStatDate,
    }));
    this.downloadCsv('fortune-stats-machine-details.csv', rows);
  }

  printReport(): void {
    window.print();
  }

  openDatepicker(datepicker: NgbInputDatepicker, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    if (!datepicker.isOpen()) {
      datepicker.open();
    }

    datepicker.navigateTo({
      year: this.currentMonth.year,
      month: this.currentMonth.month,
      day: this.currentMonth.day,
    });
  }

  private refreshCharts(): void {
    const topRows = this.rows.slice(0, 8);
    const labels = topRows.map((row) => row.groupLabel.length > 18 ? `${row.groupLabel.slice(0, 18)}…` : row.groupLabel);

    this.comparisonChartOptions = {
      series: [
        { name: 'Cash In', data: topRows.map((row) => Number(row.cashIn.toFixed(2))) },
        { name: 'Net', data: topRows.map((row) => Number(row.net.toFixed(2))) }
      ],
      chart: { type: 'bar', height: 320, toolbar: { show: false } },
      plotOptions: { bar: { borderRadius: 4, columnWidth: '42%' } },
      dataLabels: { enabled: false },
      stroke: { show: true, width: 1, colors: ['transparent'] },
      xaxis: { categories: labels },
      legend: { position: 'top' },
      colors: ['#556ee6', '#34c38f']
    };

    this.distributionChartOptions = {
      series: topRows.map((row) => row.reads),
      chart: { type: 'donut', height: 320 },
      labels,
      legend: { position: 'bottom' },
      dataLabels: { enabled: true },
      colors: ['#556ee6', '#34c38f', '#f1b44c', '#50a5f1', '#f46a6a', '#74788d', '#2a3042', '#6f42c1']
    };

    const trendMap = new Map<string, { cashIn: number; net: number }>();
    this.rows.forEach((row) => {
      const current = trendMap.get(row.periodLabel) ?? { cashIn: 0, net: 0 };
      current.cashIn += row.cashIn;
      current.net += row.net;
      trendMap.set(row.periodLabel, current);
    });

    const trendLabels = Array.from(trendMap.keys()).sort();
    this.trendChartOptions = {
      series: [
        { name: 'Cash In', data: trendLabels.map((label) => Number((trendMap.get(label)?.cashIn || 0).toFixed(2))) },
        { name: 'Net', data: trendLabels.map((label) => Number((trendMap.get(label)?.net || 0).toFixed(2))) }
      ],
      chart: { type: 'line', height: 320, toolbar: { show: false } },
      stroke: { width: 3, curve: 'smooth' },
      dataLabels: { enabled: false },
      xaxis: { categories: trendLabels },
      colors: ['#f1b44c', '#34c38f']
    };
  }

  private buildMachineDetails(row: AggregatedStatsRow): MachineDetailRow[] {
    const currentGroupBy = this.filters.get('groupBy')?.value as GroupByMode;
    const currentPeriod = this.filters.get('period')?.value as PeriodMode;
    const relevant = (this.mergedStats || []).filter((item) =>
      this.formatPeriod(item.statDate, currentPeriod) === row.periodLabel &&
      this.resolveGroupKey(item, currentGroupBy) === row.groupKey
    );

    const map = new Map<string, MachineDetailRow>();
    relevant.forEach((item) => {
      const boardId = item.boardId || 'unknown';
      const board = this.boardsMap.get(boardId);
      const key = boardId;
      if (!map.has(key)) {
        map.set(key, {
          key,
          boardId,
          boardLabel: item.assetTag || item.serialNumber || board?.assetTag || board?.serialNumber || 'Unknown board',
          locationName: item.locationName || board?.location?.locationName || board?.currentDeployment?.location?.locationName || 'Unknown location',
          salesRepName: item.salesRepName || `${board?.location?.salesRep?.firstName || board?.salesRep?.firstName || ''} ${board?.location?.salesRep?.lastName || board?.salesRep?.lastName || ''}`.trim() || 'Unassigned sales rep',
          gameName: this.getStatGameName(item, board),
          gameVersion: this.getStatGameVersion(item, board),
          coinIn: 0,
          coinOut: 0,
          cashIn: 0,
          cashOut: 0,
          gross: 0,
          gamesPlayed: 0,
          gamesWon: 0,
          reads: 0,
          net: 0,
          lastStatDate: item.statDate,
        });
      }

      const detail = map.get(key)!;
      detail.coinIn += Number(item.coinIn || 0);
      detail.coinOut += Number(item.coinOut || 0);
      detail.cashIn += Number(item.cashIn || 0);
      detail.cashOut += Number(item.cashOut || 0);
      detail.gross += Number(item.cashIn || 0) - Number(item.cashOut || 0);
      detail.gamesPlayed += Number(item.gamesPlayed || 0);
      detail.gamesWon += Number(item.gamesWon || 0);
      detail.reads += Number(item.reads || 0);
      detail.net += Number(item.net ?? (Number(item.cashIn || 0) - Number(item.cashOut || 0)));
      if (item.statDate > detail.lastStatDate) {
        detail.lastStatDate = item.statDate;
      }
    });

    return Array.from(map.values()).sort((a, b) => b.cashIn - a.cashIn);
  }

  private downloadCsv(fileName: string, rows: Record<string, any>[]): void {
    if (!rows.length) {
      this.toastr.info('No data to export');
      return;
    }

    const headers = Object.keys(rows[0]);
    const csv = [
      headers.join(','),
      ...rows.map((row) => headers.map((header) => this.escapeCsvValue(row[header])).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(link.href);
  }

  private escapeCsvValue(value: any): string {
    const safe = String(value ?? '');
    return `"${safe.replace(/"/g, '""')}"`;
  }

  private includesToday(from: string, to: string): boolean {
    const today = new Date().toISOString().slice(0, 10);
    return (!from || from <= today) && (!to || to >= today);
  }

  private toDateStruct(date: Date): NgbDateStruct {
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
    };
  }

  private toIsoDate(value: string | NgbDateStruct | null | undefined): string {
    if (!value) {
      return '';
    }

    if (typeof value === 'string') {
      return value;
    }

    const year = Number(value.year);
    const month = Number(value.month);
    const day = Number(value.day);
    if (!year || !month || !day) {
      return '';
    }

    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  private mergeTodayLive(stats: FusionConnectDailyStat[], reads: FusionConnectRead[]): FusionConnectDailyStat[] {
    const today = new Date().toISOString().slice(0, 10);
    const todaysReads = (reads || []).filter((item) => this.toDateKey(item?.timeStamp) === today);
    if (!todaysReads.length) {
      return stats;
    }

    const grouped = new Map<string, FusionConnectRead[]>();
    todaysReads.forEach((read) => {
      const boardId = read.boardId || read.board?.id;
      if (!boardId) {
        return;
      }
      const items = grouped.get(boardId) ?? [];
      items.push(read);
      grouped.set(boardId, items);
    });

    const liveRows: FusionConnectDailyStat[] = [];
    grouped.forEach((items, boardId) => {
      items.sort((a, b) => new Date(a.timeStamp || '').getTime() - new Date(b.timeStamp || '').getTime());
      const first = items[0];
      const last = items[items.length - 1];
      const board = last.board || this.boardsMap.get(boardId);

      const cashIn = this.diff(last.cashIn, first.cashIn);
      const cashOut = this.diff(last.cashOut, first.cashOut);
      const coinIn = this.diff(last.coinIn, first.coinIn);
      const coinOut = this.diff(last.coinOut, first.coinOut);
      const gamesPlayed = this.diff(last.gamesPlayed, first.gamesPlayed);
      const gamesWon = this.diff(last.gamesWon, first.gamesWon);

      liveRows.push({
        statDate: today,
        boardId,
        assetTag: board?.assetTag,
        serialNumber: board?.serialNumber,
        locationId: last.locationId || last.location?.id || board?.location?.id || board?.currentDeployment?.location?.id,
        locationName: last.locationName || last.location?.locationName || board?.location?.locationName || board?.currentDeployment?.location?.locationName,
        salesRepId: last.salesRepId || last.location?.salesRep?.id || board?.location?.salesRep?.id || board?.salesRep?.id,
        salesRepName: last.salesRepName || `${last.location?.salesRep?.firstName || board?.location?.salesRep?.firstName || board?.salesRep?.firstName || ''} ${last.location?.salesRep?.lastName || board?.location?.salesRep?.lastName || board?.salesRep?.lastName || ''}`.trim() || undefined,
        gamePackageId: last.gamePackageId || last.gamePackage?.id || board?.installedGamePackage?.id || board?.currentDeployment?.installedGamePackage?.id,
        gamePackageName: last.gamePackageName || last.gamePackage?.name || board?.installedGamePackage?.name || board?.currentDeployment?.installedGamePackage?.name,
        gamePackageVersion: last.gamePackageVersion || last.gamePackage?.version || board?.installedGamePackage?.version || board?.currentDeployment?.installedGamePackage?.version,
        coinIn,
        coinOut,
        cashIn,
        cashOut,
        gamesPlayed,
        gamesWon,
        net: cashIn - cashOut,
        reads: items.length,
      });
    });

    return [...stats.filter((item) => item.statDate !== today), ...liveRows];
  }

  private aggregateStats(
    stats: FusionConnectDailyStat[],
    groupBy: GroupByMode,
    period: PeriodMode
  ): AggregatedStatsRow[] {
    const map = new Map<string, AggregatedStatsRow & { boardIds: Set<string> }>();

    (stats || []).forEach((item) => {
      const periodLabel = this.formatPeriod(item.statDate, period);
      const groupKey = this.resolveGroupKey(item, groupBy);
      const groupLabel = this.resolveGroupLabel(item, groupBy);
      const key = `${periodLabel}__${groupKey}`;

      if (!map.has(key)) {
        map.set(key, {
          key,
          periodLabel,
          groupKey,
          groupLabel,
          gameLabel: 'N/A',
          boardCount: 0,
          coinIn: 0,
          coinOut: 0,
          cashIn: 0,
          cashOut: 0,
          gross: 0,
          gamesPlayed: 0,
          gamesWon: 0,
          reads: 0,
          net: 0,
          boardIds: new Set<string>(),
        });
      }

      const row = map.get(key)!;
      if (item.boardId) {
        row.boardIds.add(item.boardId);
        row.boardCount = row.boardIds.size;
      }
      row.coinIn += Number(item.coinIn || 0);
      row.coinOut += Number(item.coinOut || 0);
      row.cashIn += Number(item.cashIn || 0);
      row.cashOut += Number(item.cashOut || 0);
      row.gross += Number(item.cashIn || 0) - Number(item.cashOut || 0);
      row.gamesPlayed += Number(item.gamesPlayed || 0);
      row.gamesWon += Number(item.gamesWon || 0);
      row.reads += Number(item.reads || 0);
      row.net += Number(item.net ?? (Number(item.cashIn || 0) - Number(item.cashOut || 0)));
      row.gameLabel = this.resolveAggregateGameLabel(row.gameLabel, item);
    });

    return Array.from(map.values())
      .map(({ boardIds, ...row }) => row)
      .sort((a, b) => {
        if (a.periodLabel === b.periodLabel) {
          return a.groupLabel.localeCompare(b.groupLabel);
        }
        return b.periodLabel.localeCompare(a.periodLabel);
      });
  }

  private resolveGroupKey(item: FusionConnectDailyStat, groupBy: GroupByMode): string {
    const board = item.boardId ? this.boardsMap.get(item.boardId) : undefined;

    if (groupBy === 'board') {
      return item.boardId || item.assetTag || item.serialNumber || board?.id || 'unknown-board';
    }

    if (groupBy === 'salesRep') {
      return item.salesRepId || item.salesRepName || board?.location?.salesRep?.id || board?.salesRep?.id || 'unknown-salesrep';
    }

    return item.locationId || item.locationName || board?.location?.id || board?.currentDeployment?.location?.id || 'unknown-location';
  }

  private resolveGroupLabel(item: FusionConnectDailyStat, groupBy: GroupByMode): string {
    const board = item.boardId ? this.boardsMap.get(item.boardId) : undefined;

    if (groupBy === 'board') {
      return item.assetTag || item.serialNumber || board?.assetTag || board?.serialNumber || 'Unknown board';
    }

    if (groupBy === 'salesRep') {
      return item.salesRepName || 'Unassigned sales rep';
    }

    return item.locationName || board?.location?.locationName || board?.currentDeployment?.location?.locationName || 'Unknown location';
  }

  private resolveAggregateGameLabel(currentLabel: string, item: FusionConnectDailyStat): string {
    const board = item.boardId ? this.boardsMap.get(item.boardId) : undefined;
    const nextLabel = this.getStatGameName(item, board);

    if (!nextLabel || nextLabel === 'N/A') {
      return currentLabel;
    }

    if (!currentLabel || currentLabel === 'N/A') {
      return nextLabel;
    }

    return currentLabel === nextLabel ? currentLabel : 'Multiple games';
  }

  private getStatGameName(item: FusionConnectDailyStat, board?: Board): string {
    return item.gamePackageName || board?.installedGamePackage?.name || board?.currentDeployment?.installedGamePackage?.name || 'N/A';
  }

  private getStatGameVersion(item: FusionConnectDailyStat, board?: Board): string {
    return item.gamePackageVersion || board?.installedGamePackage?.version || board?.currentDeployment?.installedGamePackage?.version || '';
  }

  private formatPeriod(value: string, period: PeriodMode): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    if (period === 'yearly') {
      return `${date.getFullYear()}`;
    }

    if (period === 'monthly') {
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    }

    if (period === 'weekly') {
      const copy = new Date(date);
      const day = (copy.getDay() + 6) % 7;
      copy.setDate(copy.getDate() - day);
      return `Week of ${copy.toISOString().slice(0, 10)}`;
    }

    return value;
  }

  private toDateKey(value?: string): string {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    return date.toISOString().slice(0, 10);
  }

  private diff(last: any, first: any): number {
    const end = Number(last || 0);
    const start = Number(first || 0);
    return Math.max(end - start, 0);
  }
}
